import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Briefcase, Search, ArrowLeft, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function Projects() {
  const { name } = useParams();
  if (name) return <ProjectDetail name={decodeURIComponent(name)} />;
  return <ProjectsList />;
}

function ProjectsList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((it) => !ql || it.project.toLowerCase().includes(ql));
  }, [items, q]);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
            PROJEKTE · {items.length}
          </div>
          <h1 className="font-display text-4xl uppercase flex items-center gap-3">
            <Briefcase className="w-8 h-8" /> Projekte
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Alle Bauvorhaben mit Materialbewegungen. Klicke auf ein Projekt für Detailansicht und Nachkalkulation.
          </p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Projekt suchen…"
          data-testid="projects-search-input"
          className="pl-10 rounded-sm border-zinc-400 h-11"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">PROJEKT</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">ENTNOMMEN</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">ZURÜCK</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">NETTO</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">EK-WERT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">LETZTE BEW.</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em] w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 mono text-xs text-zinc-500">LADEN…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-sm text-zinc-500">
                  Noch keine Projekte mit Bewegungen.
                </td></tr>
              ) : filtered.map((p) => (
                <tr key={p.project} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`project-row-${p.project}`}>
                  <td className="px-4 py-3">
                    <Link to={`/projekte/${encodeURIComponent(p.project)}`} className="font-medium hover:text-[#F97316]">
                      {p.project}
                    </Link>
                    <div className="mono text-[11px] text-zinc-500">
                      {p.articles_count} Artikel · {p.movements_count} Bew.
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-red-700">{p.total_entnommen}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-blue-700">{p.total_zurueck}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-bold">{p.net_qty}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold">€ {fmtEur(p.ek_value)}</td>
                  <td className="px-4 py-3 mono text-xs text-zinc-500">
                    {p.last_at ? new Date(p.last_at).toLocaleString("de-DE") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/projekte/${encodeURIComponent(p.project)}`} className="inline-flex p-2 hover:bg-zinc-100 rounded-sm">
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProjectDetail({ name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/projects/${encodeURIComponent(name)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [name]);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Artikel", "EAN", "Entnommen", "Zurueck", "Netto", "EK-Preis", "EK-Wert", "VK-Wert"];
    const rows = data.items.map((it) => [
      it.name, it.ean || "",
      it.entnommen, it.zurueck, it.net,
      it.ek_price.toFixed(2).replace(".", ","),
      it.ek_value.toFixed(2).replace(".", ","),
      it.vk_value.toFixed(2).replace(".", ","),
    ]);
    rows.push([]);
    rows.push(["GESAMT EK-WERT", "", "", "", "", "", data.total_ek_value.toFixed(2).replace(".", ","), ""]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `projekt_${name.replace(/[^a-z0-9-_]/gi, "_")}.csv`;
    a.click();
    toast.success("CSV exportiert.");
  };

  if (loading) return <div className="mono text-sm text-zinc-500">LADEN…</div>;
  if (!data) return <div className="text-zinc-500">Projekt nicht gefunden.</div>;

  return (
    <div className="max-w-[1600px] mx-auto space-y-6" data-testid="project-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/projekte" className="inline-flex items-center text-sm text-zinc-600 hover:text-black">
          <ArrowLeft className="w-4 h-4 mr-1" /> Alle Projekte
        </Link>
        <Button
          onClick={exportCsv}
          variant="outline"
          data-testid="project-export-csv"
          className="rounded-sm border-zinc-400 h-10"
        >
          <Download className="w-4 h-4 mr-2" /> CSV-Nachkalkulation
        </Button>
      </div>

      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">PROJEKT</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase break-words">{name}</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Bewegungen" value={data.movements_count} />
        <Metric label="Artikel" value={data.articles_count} />
        <Metric label="EK-Wert" value={`€ ${fmtEur(data.total_ek_value)}`} color="#F97316" />
        <Metric label="Status" value={data.movements_count > 0 ? "Aktiv" : "Leer"} color="#16A34A" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 bg-zinc-900 text-white">
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-400">MATERIAL-NACHKALKULATION</div>
          <div className="font-display text-lg uppercase">Artikel je Projekt</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600">
              <tr>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">ARTIKEL</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">ENTN.</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">ZURÜCK</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">NETTO</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">EK / STK</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">EK-WERT</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">VK-WERT</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">Keine Artikel.</td></tr>
              ) : data.items.map((it) => (
                <tr key={it.article_id} className="border-t border-zinc-100">
                  <td className="px-4 py-2.5">
                    <Link to={`/artikel/${it.article_id}`} className="font-medium hover:text-[#F97316]">
                      {it.name}
                    </Link>
                    <div className="mono text-[11px] text-zinc-500">{it.ean}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold text-red-700">{it.entnommen}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-blue-700">{it.zurueck}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-bold">{it.net}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">€ {fmtEur(it.ek_price)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-semibold">€ {fmtEur(it.ek_value)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-zinc-600">€ {fmtEur(it.vk_value)}</td>
                </tr>
              ))}
            </tbody>
            {data.items.length > 0 && (
              <tfoot className="bg-zinc-100 font-semibold">
                <tr>
                  <td className="px-4 py-3 mono text-xs uppercase tracking-wider" colSpan={5}>Gesamt EK-Wert</td>
                  <td className="px-4 py-3 text-right font-mono-num text-lg">€ {fmtEur(data.total_ek_value)}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200">
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">BEWEGUNGEN</div>
          <div className="font-display text-lg uppercase">Verlauf (max. 300)</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">ZEITPUNKT</th>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">TYP</th>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">ARTIKEL</th>
                <th className="text-right px-4 py-2 mono text-[10px] tracking-wider">MENGE</th>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">LAGER</th>
                <th className="text-left px-4 py-2 mono text-[10px] tracking-wider">BENUTZER</th>
              </tr>
            </thead>
            <tbody>
              {data.movements.map((m) => (
                <tr key={m.id} className="border-t border-zinc-100">
                  <td className="px-4 py-2 mono text-xs">{new Date(m.at).toLocaleString("de-DE")}</td>
                  <td className="px-4 py-2 mono text-[10px] uppercase tracking-wider">{m.type || "—"}</td>
                  <td className="px-4 py-2 text-sm">{m.article_name}</td>
                  <td className={`px-4 py-2 text-right font-mono-num font-semibold ${m.delta > 0 ? "text-green-700" : "text-red-700"}`}>
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </td>
                  <td className="px-4 py-2 mono text-xs">{m.location_label || m.location || "—"}</td>
                  <td className="px-4 py-2 mono text-xs text-zinc-500">{m.user_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color = "#09090B" }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-mono-num mt-1">{value}</div>
    </div>
  );
}
